import {
  basePageToServer,
  TrainingPhraseConfig,
  trainingPhraseToLocal,
  promptInfoToServer,
  trainingPhraseToServer,
  ValueSuggestionData,
  valueSuggestionToLocal,
  valueSuggestionToServer,
  ValueCheck,
  valueCheckToLocal,
  valueCheckToServer,
  DialogAct,
  dialogActToLocal,
  dialogActToServer,
  ConfirmationInfo,
  confirmationInfoToServer,
  confirmationInfoToLocal,
  FunctionParameter,
  SlotStorageAnnotation,
  slotStorageAnnotationToLocal,
  slotStorageAnnotationToServer,
  PostFillAction,
  postFillActionToLocal,
  postFillActionToServer,
  ActionChannel,
  FrameItem
} from '@interface/index';
import { BasePageModel, basePageToLocal } from './base';
import { SlotType } from './slot';
import { DiffStates } from './version';
import { PromptInfo, promptInfoToLocal } from './prompt';
import { AttributeCompanion, attributeCompanionToLocal, attributeCompanionToServer } from './companion';
import { getNextColor, SourceType, AskStrategy, RESPONSE_CHANNEL_TYPE, FillMode, MessagingType } from '@config/index';
import {
  TriggerCondition,
  conditionToLocal,
  conditionToServer
} from './response';
import { ParameterMapStore } from '@lib/index';
import integration from '@page/agent/integration';

const DefaultInformChannels: ActionChannel[] = [
  {
    tag: RESPONSE_CHANNEL_TYPE.RESTFUL,
    actions: []
  }
];

export interface ParameterMapConfig {
  [key: string]: ParameterConfig;
}

export enum ParameterType {
  ENTITY = 'entity',
  ATTRIBUTE = 'attribute',
  COMPOSITE = 'composite',
  SERVICE = 'service',
  CLOSURE = 'closure',
  INTENT = 'intent',
  COMPANION = 'companion',
  CONNECTION = 'connection',
  GENERIC_TYPE = 'generic_type',
  EXTERNAL_TYPE = 'external_type',
  FUNCTION_PARAM = 'function_param',
  CONTENT = 'content',
  UNKNOWN = ''
}

export const slotTypeToParameterType = (slotType: SlotType) => {
  switch (slotType) {
    case SlotType.intent:
      return ParameterType.INTENT;
    case SlotType.composite:
      return ParameterType.COMPOSITE;
    case SlotType.entity:
      return ParameterType.ENTITY;
    case SlotType.generic:
      return ParameterType.GENERIC_TYPE;
    case SlotType.external:
      return ParameterType.EXTERNAL_TYPE;
    default:
      return ParameterType.UNKNOWN;
  }
};

export const parameterTypeToSlotType = (parameterType: string) => {
  switch (parameterType) {
    case ParameterType.INTENT:
      return SlotType.intent;
    case ParameterType.COMPOSITE:
      return SlotType.composite;
    case ParameterType.ENTITY:
      return SlotType.entity;
    case ParameterType.GENERIC_TYPE:
      return SlotType.generic;
    case ParameterType.EXTERNAL_TYPE:
      return SlotType.external;
    default:
      return SlotType.unknown;
  }
};

export type SlotInitialization = BasePageModel & {
  target?: Reference;
  association: string;
  associationComment?: string;
  masterSelf?: SlotInitialization
};

export function slotInitializationToLocal(res: any): SlotInitialization {
  return {
    ...basePageToLocal(res),
    target: res.target,
    association: res.association,
    associationComment: res.association_comment,
    masterSelf: res.master_self && slotInitializationToLocal(res.master_self)
  };
}

export function slotInitializationToServer(data: SlotInitialization) {
  return {
    ...basePageToServer(data),
    target: data.target,
    association: data.association,
    association_comment: data.associationComment,
    to_be_deleted: data.toBeDeleted
  };
}

export type Filtering = BasePageModel & {
  sourceSlot?: Reference;
  filteringExpression: string;
  informPrompts: PromptInfo[];
  masterSelf?: Filtering;
};

export function filteringToLocal(res: any): Filtering {
  return {
    ...basePageToLocal(res),
    sourceSlot: res.source_slot && res.source_slot.map(referenceNodeToLocal),
    filteringExpression: res.filtering_expression,
    informPrompts: res.inform_prompts && res.inform_prompts.map(promptInfoToLocal),
    masterSelf: res.master_self && filteringToLocal(res.master_self)
  };
}

export function filteringToServer(data: Filtering) {
  return {
    ...basePageToServer(data),
    source_slot: data.sourceSlot && data.sourceSlot.map(referenceNodeToServer),
    filtering_expression: data.filteringExpression,
    inform_prompts: data.informPrompts && data.informPrompts.map(promptInfoToServer),
    to_be_deleted: data.toBeDeleted
  };
}

export interface ParameterConfig extends BasePageModel {
  type: ParameterType;
  // prompts: string[];
  promptQuestions?: PromptInfo[];
  triggers?: string[];
  parentId?: string;
  proto?: {
    type: ParameterType;
    id: string;
    name: string;
    isGlobal?: boolean;
  };
  parameters?: FunctionParameter[];
  multiValue?: boolean;
  attributes?: any[];
  recognizer?: string[];
  isGlobal?: boolean;
  isInterface?: boolean;
  isStatic?: boolean;
  external?: boolean;
  inheritedFrames?: FrameItem[];
  cuiDisabled?: boolean;
}

export interface Attribute extends ParameterConfig {
  customizationPath?: Reference;
  implementation?: ReferenceNode;
  index?: number;
  triggers: string[];
  multiValue?: boolean;
  multiValueMin: number;
  multiValueMax: number;
  allowMultiValuePrompt?: boolean;
  allowDontCare?: boolean;
  allowPrompt?: boolean;
  allowConfirm?: boolean;
  allowMultiValuedConfirm?: boolean;
  allowExpression?: boolean;
  allowValueSuggestion?: boolean;
  allowValueCheck?: boolean;
  allowInitialization?: boolean;
  enableFiltering?: boolean;
  allowDialogAct?: boolean;
  allowPostFillAction?: boolean;
  allowSlotUpdate?: boolean;
  allowCompanion?: boolean;
  enableValueExpression?: boolean;
  noMemory?: boolean;
  dataQueryId?: string;
  version?: string;
  isGlobal?: boolean;
  parameterCompanions: AttributeCompanion[];
  promptExpressions?: TrainingPhraseConfig[];
  valueExpressions?: TrainingPhraseConfig[];
  confirmations: ConfirmationInfo[];
  multiValuedConfirmations: ConfirmationInfo[];
  multiValueQuestions?: PromptInfo[];
  multiValueExpressionsNo?: TrainingPhraseConfig[];
  multiValueMinExceptions?: PromptInfo[];
  multiValueMaxExceptions?: PromptInfo[];
  dontCareExpressions?: TrainingPhraseConfig[];
  booleanGatePrompts?: string[];
  booleanGateExpressionsYes?: TrainingPhraseConfig[];
  booleanGateExpressionsNo?: TrainingPhraseConfig[];
  slotUpdateExpressions?: TrainingPhraseConfig[];
  slotAppendExpressions?: TrainingPhraseConfig[];
  slotDeleteExpressions?: TrainingPhraseConfig[];
  negativeExpressions?: TrainingPhraseConfig[];
  entailmentExpressions?: TrainingPhraseConfig[];
  abortPhrases: TrainingPhraseConfig[];
  abortNegativePhrases: TrainingPhraseConfig[];
  abortEntailmentPhrases: TrainingPhraseConfig[];
  fillMode: FillMode;
  messagingType: MessagingType;
  fillWithNaturalLanguage?: boolean;
  askStrategy: AskStrategy;
  askConditionType: SourceType;
  askConditions: TriggerCondition[];
  askCodeCondition: string;
  conditionComment?: string;
  valueSuggestions: ValueSuggestionData[];
  valueChecks: ValueCheck[];
  informPrompts: PromptInfo[];
  initializations: SlotInitialization[];
  filterings: Filtering[];
  auxiliary?: boolean;
  dialogActs: DialogAct[];
  postFillActions: PostFillAction[];
  storageAnnotation?: SlotStorageAnnotation;
  booleanGated?: boolean;
  gatedType?: ReferenceNode;
  masterSelf?: Attribute;
}

export const defaultSelectedParameter: Attribute = {
  id: '',
  name: '',
  type: ParameterType.COMPOSITE,
  triggers: [],
  description: '',
  promptQuestions: [],
  multiValueMin: 0,
  multiValueMax: 99,
  state: DiffStates.NORMAL,
  parameterCompanions: [],
  fillMode: FillMode.SYNC,
  messagingType: MessagingType.SIMPLE,
  fillWithNaturalLanguage: false,
  askStrategy: AskStrategy.ALWAYS_ASK,
  askConditionType: SourceType.CODE_EXPRESSION,
  askConditions: [],
  askCodeCondition: '',
  valueSuggestions: [],
  valueChecks: [],
  confirmations: [],
  multiValuedConfirmations: [],
  informPrompts: [],
  initializations: [],
  filterings: [],
  dialogActs: [],
  postFillActions: [],
  abortPhrases: [],
  abortEntailmentPhrases: [],
  abortNegativePhrases: []
};

export interface ParameterUpdateFuncArgumentConfig {
  nextSelectedParameters?: Attribute[];
  nextParameters?: ParameterConfig[];
  nextRevertParameterID?: string;
  nextDelParameter?: Attribute;
  draggingParameterId?: string;
}

export interface ReferenceNode {
  id: string;
  type: ParameterType;
  name: string;
  showIndex?: boolean;
  index?: number;
}

export type ServiceSlot = BasePageModel & {
  label: string;
  type?: ReferenceNode;
  integrationLabel?: string;
}; // wrapper for ReferenceNode

export type Reference = ReferenceNode[];

export function attributeToLocal(parameter: any, isMasterSelf?: boolean): Attribute {

  let target: Attribute;

  const attrId = parameter.id || parameter.attribute_id;

  // if (attrId) {
  let proto: any = attrId && ParameterMapStore.get(attrId);

  const protoId = parameter.type && parameter.type.id;
  proto = protoId && ParameterMapStore.get(protoId);

  if (parameter.attribute_companions && !isMasterSelf) {

    const kvs: { [key: string]: ParameterConfig } = {};
    for (const cp of parameter.attribute_companions) {
      kvs[cp.id] = {
        id: cp.id,
        name: `${parameter.name}_${cp.name}`,
        type: ParameterType.COMPANION,
        promptQuestions: [],
        multiValue: cp.multi_value
      };
    }
    ParameterMapStore.setMany(kvs);
  }

  target = {
    ...basePageToLocal(parameter),
    type: ParameterType.ATTRIBUTE,
    triggers: parameter.triggers,
    descrition: parameter.descrition,
    attributes: proto && proto.attributes || [],
    proto: {
      id: parameter.type && parameter.type.id,
      type: slotTypeToParameterType(parameter.type &&
        parameter.type.type),
      name: parameter.type && parameter.type.name,
      isGlobal: proto && proto.isGlobal
    },
    customizationPath: parameter.customization_path && parameter.customization_path.map(referenceNodeToLocal),
    implementation: parameter.implementation && referenceNodeToLocal(parameter.implementation),
    isGlobal: parameter.isGlobal,
    multiValue: parameter.multi_value || false,
    multiValueMin: parameter.multi_value_min,
    multiValueMax: parameter.multi_value_max,
    allowMultiValuePrompt: parameter.allow_multi_value_prompt || false,
    allowDontCare: parameter.allow_dont_care || false,
    allowPrompt: parameter.allow_prompt || false,
    allowConfirm: parameter.allow_confirm || false,
    allowMultiValuedConfirm: parameter.allow_multi_valued_confirm,
    allowExpression: parameter.allow_expression || false,
    allowValueSuggestion: parameter.allow_value_suggestion || false,
    allowValueCheck: parameter.allow_value_check || false,
    allowInitialization: parameter.allow_initialization || false,
    enableFiltering: parameter.enable_filtering,
    allowDialogAct: parameter.allow_dialog_act || false,
    allowPostFillAction: parameter.allow_post_fill_action || false,
    allowSlotUpdate: parameter.allow_slot_update,
    allowCompanion: parameter.allow_companion || false,
    enableValueExpression: parameter.enable_value_expression || false,
    promptQuestions: parameter.prompt_questions &&
      parameter.prompt_questions.map(promptInfoToLocal),
    version: parameter.version,
    parameterCompanions: parameter.attribute_companions &&
      parameter.attribute_companions.map(attributeCompanionToLocal) || [],
    promptExpressions:
      parameter.prompt_expressions &&
      parameter.prompt_expressions.map(trainingPhraseToLocal),
    valueExpressions:
      parameter.value_expressions &&
      parameter.value_expressions.map(trainingPhraseToLocal),
    confirmations: parameter.confirmations &&
      parameter.confirmations.map(confirmationInfoToLocal),
    multiValuedConfirmations: parameter.multi_valued_confirmations &&
      parameter.multi_valued_confirmations.map(confirmationInfoToLocal),
    multiValueQuestions: parameter.multi_value_questions &&
      parameter.multi_value_questions.map(promptInfoToLocal),
    multiValueExpressionsNo:
      parameter.multi_value_expressions_no &&
      parameter.multi_value_expressions_no.map(trainingPhraseToLocal),
    multiValueMinExceptions:
      parameter.multi_value_min_exceptions &&
      parameter.multi_value_min_exceptions.map(promptInfoToLocal),
    multiValueMaxExceptions:
      parameter.multi_value_max_exceptions &&
      parameter.multi_value_max_exceptions.map(promptInfoToLocal),
    dontCareExpressions:
      parameter.dont_care_expressions &&
      parameter.dont_care_expressions.map(trainingPhraseToLocal),
    booleanGatePrompts: parameter.boolean_gate_prompts,
    booleanGateExpressionsYes: parameter.boolean_gate_expressions_yes &&
      parameter.boolean_gate_expressions_yes.map(trainingPhraseToLocal),
    booleanGateExpressionsNo: parameter.boolean_gate_expressions_no &&
      parameter.boolean_gate_expressions_no.map(trainingPhraseToLocal),
    slotUpdateExpressions: parameter.slot_update_expressions &&
      parameter.slot_update_expressions.map(trainingPhraseToLocal),
    slotAppendExpressions: parameter.slot_append_expressions &&
      parameter.slot_append_expressions.map(trainingPhraseToLocal),
    slotDeleteExpressions: parameter.slot_delete_expressions &&
      parameter.slot_delete_expressions.map(trainingPhraseToLocal),
    negativeExpressions: parameter.negative_expressions &&
      parameter.negative_expressions.map(trainingPhraseToLocal),
    entailmentExpressions: parameter.entailment_expressions &&
      parameter.entailment_expressions.map(trainingPhraseToLocal),
    abortPhrases: parameter.abort_expressions
      && parameter.abort_expressions.map(trainingPhraseToLocal),
    abortNegativePhrases: parameter.abort_negative_expressions
      && parameter.abort_negative_expressions.map(trainingPhraseToLocal),
    abortEntailmentPhrases: parameter.abort_entailment_expressions
      && parameter.abort_entailment_expressions.map(trainingPhraseToLocal),
    fillMode: parameter.fill_mode || FillMode.SYNC,
    messagingType: parameter.messaging_type || MessagingType.SIMPLE,
    fillWithNaturalLanguage: parameter.fill_with_natural_language || false,
    askStrategy: parameter.ask_strategy || AskStrategy.ALWAYS_ASK,
    askConditionType: parameter.ask_condition_type || SourceType.CODE_EXPRESSION,
    askConditions: parameter.ask_conditions &&
      parameter.ask_conditions.map(conditionToLocal),
    askCodeCondition: parameter.ask_code_condition || '',
    conditionComment: parameter.condition_comment,
    valueSuggestions: parameter.value_suggestions &&
      parameter.value_suggestions.map(valueSuggestionToLocal),
    valueChecks: parameter.value_checks
      && parameter.value_checks.map(valueCheckToLocal),
    dialogActs: parameter.dialog_acts &&
      parameter.dialog_acts.map(dialogActToLocal),
    postFillActions: parameter.post_fill_actions &&
      parameter.post_fill_actions.map(postFillActionToLocal),
    dataQueryId: parameter.data_query_id,
    informPrompts: parameter.inform_prompts && parameter.inform_prompts.map(promptInfoToLocal),
    color:
      (proto && (proto.type === ParameterType.ENTITY
        || proto.type === ParameterType.COMPOSITE))
        ? getNextColor()
        : '',
    initializations: (parameter.initializations || []).map(slotInitializationToLocal),
    filterings: (parameter.filterings || []).map(filteringToLocal),
    auxiliary: parameter.auxiliary,
    booleanGated: parameter.boolean_gated,
    gatedType: referenceNodeToLocal(parameter.gated_type),
    storageAnnotation: slotStorageAnnotationToLocal(parameter.storage_annotation),
    masterSelf: parameter.master_self && attributeToLocal(parameter.master_self, true)
  } as any;

  if (!isMasterSelf) {
    ParameterMapStore.set(attrId, target);
  }

  return target;
}

export function attributeToServer(parameter: Attribute) {

  return {
    ...basePageToServer(parameter),
    id: parameter.id,
    owner_type: parameter.ownerType,
    name: parameter.name,
    owner_id: parameter.ownerId,
    type: parameter.proto && {
      id: parameter.proto.id,
      type: parameterTypeToSlotType(parameter.proto.type)
    },
    customization_path: parameter.customizationPath && parameter.customizationPath.map(referenceNodeToServer),
    implementation: parameter.implementation && referenceNodeToServer(parameter.implementation),
    triggers: parameter.triggers,
    multi_value: parameter.multiValue,
    multi_value_min: parameter.multiValueMin,
    multi_value_max: parameter.multiValueMax,
    allow_multi_value_prompt: parameter.allowMultiValuePrompt,
    allow_dont_care: parameter.allowDontCare,
    allow_prompt: parameter.allowPrompt,
    allow_confirm: parameter.allowConfirm,
    allow_multi_valued_confirm: parameter.allowMultiValuedConfirm,
    allow_expression: parameter.allowExpression,
    allow_value_suggestion: parameter.allowValueSuggestion,
    allow_value_check: parameter.allowValueCheck,
    allow_initialization: parameter.allowInitialization,
    enable_filtering: parameter.enableFiltering,
    allow_dialog_act: parameter.allowDialogAct,
    allow_post_fill_action: parameter.allowPostFillAction,
    allow_slot_update: parameter.allowSlotUpdate,
    allow_companion: parameter.allowCompanion,
    enable_value_expression: parameter.enableValueExpression,
    prompt_questions: parameter.promptQuestions &&
      parameter.promptQuestions.map(promptInfoToServer),
    prompt_expressions: parameter.promptExpressions &&
      parameter.promptExpressions.map(trainingPhraseToServer),
    value_expressions: parameter.valueExpressions &&
      parameter.valueExpressions.map(trainingPhraseToServer),
    confirmations: parameter.confirmations &&
      parameter.confirmations.map(confirmationInfoToServer),
    multi_valued_confirmations: parameter.multiValuedConfirmations &&
      parameter.multiValuedConfirmations.map(confirmationInfoToServer),
    multi_value_questions: parameter.multiValueQuestions &&
      parameter.multiValueQuestions.map(promptInfoToServer),
    multi_value_expressions_no: parameter.multiValueExpressionsNo &&
      parameter.multiValueExpressionsNo.map(trainingPhraseToServer),
    multi_value_min_exceptions: parameter.multiValueMinExceptions &&
      parameter.multiValueMinExceptions.map(promptInfoToServer),
    multi_value_max_exceptions: parameter.multiValueMaxExceptions &&
      parameter.multiValueMaxExceptions.map(promptInfoToServer),
    dont_care_expressions: parameter.dontCareExpressions &&
      parameter.dontCareExpressions.map(trainingPhraseToServer),
    boolean_gate_prompts: parameter.booleanGatePrompts,
    boolean_gate_expressions_yes: parameter.booleanGateExpressionsYes &&
      parameter.booleanGateExpressionsYes.map(trainingPhraseToServer),
    boolean_gate_expressions_no: parameter.booleanGateExpressionsNo &&
      parameter.booleanGateExpressionsNo.map(trainingPhraseToServer),
    slot_update_expressions: parameter.slotUpdateExpressions &&
      parameter.slotUpdateExpressions.map(trainingPhraseToServer),
    slot_append_expressions: parameter.slotAppendExpressions &&
      parameter.slotAppendExpressions.map(trainingPhraseToServer),
    slot_delete_expressions: parameter.slotDeleteExpressions &&
      parameter.slotDeleteExpressions.map(trainingPhraseToServer),
    negative_expressions: parameter.negativeExpressions &&
      parameter.negativeExpressions.map(trainingPhraseToServer),
    entailment_expressions: parameter.entailmentExpressions &&
      parameter.entailmentExpressions.map(trainingPhraseToServer),
    abort_expressions: parameter.abortPhrases &&
      parameter.abortPhrases.map(trainingPhraseToServer),
    abort_negative_expressions: parameter.abortNegativePhrases &&
      parameter.abortNegativePhrases.map(trainingPhraseToServer),
    abort_entailment_expressions: parameter.abortEntailmentPhrases &&
      parameter.abortEntailmentPhrases.map(trainingPhraseToServer),
    attribute_companions: parameter.parameterCompanions &&
      parameter.parameterCompanions.map(attributeCompanionToServer) || [],
    fill_mode: parameter.fillMode || FillMode.SYNC,
    messaging_type: parameter.messagingType || MessagingType.SIMPLE,
    fill_with_natural_language: parameter.fillWithNaturalLanguage || false,
    ask_strategy: parameter.askStrategy,
    ask_condition_type: parameter.askConditionType,
    ask_conditions: parameter.askConditions &&
      parameter.askConditions.map(conditionToServer),
    ask_code_condition: parameter.askCodeCondition,
    condition_comment: parameter.conditionComment,
    value_suggestions: parameter.valueSuggestions &&
      parameter.valueSuggestions.map(valueSuggestionToServer),
    value_checks: parameter.valueChecks &&
      parameter.valueChecks.map(valueCheckToServer),
    dialog_acts: parameter.dialogActs &&
      parameter.dialogActs.map(dialogActToServer),
    post_fill_actions: parameter.postFillActions &&
      parameter.postFillActions.map(postFillActionToServer),
    data_query_id: parameter.dataQueryId,
    inform_prompts: parameter.informPrompts && parameter.informPrompts.map(promptInfoToServer),
    initializations: (parameter.initializations || []).map(slotInitializationToServer),
    filterings: (parameter.filterings || []).map(filteringToServer),
    auxiliary: parameter.auxiliary,
    version: parameter.version,
    boolean_gated: parameter.booleanGated,
    gated_type: referenceNodeToServer(parameter.gatedType),
    storage_annotation: slotStorageAnnotationToServer(parameter.storageAnnotation)
  };
}

export function referenceNodeToLocal(data: any): ReferenceNode | undefined {
  if (!data) {
    return undefined;
  }

  return {
    id: data.id,
    name: data.name,
    type: data.type,
    showIndex: data.show_index,
    index: data.index
  };
}

export function referenceNodeToServer(data?: ReferenceNode) {

  if (!data) {
    return undefined;
  }

  return {
    id: data.id,
    name: data.name,
    type: data.type,
    show_index: data.showIndex || false,
    index: data.index
  };
}


export function serviceSlotToLocal(data: any): ServiceSlot | undefined {
  if (!data) {
    return undefined;
  }

  return {
    ...basePageToLocal(data),
    label: data.label,
    type: referenceNodeToLocal(data.type),
    integrationLabel: data.integration_label,
  } as ServiceSlot;
}

export function serviceSlotToServer(data: ServiceSlot): any {
  if (!data) {
    return undefined;
  }

  return {
    id: data.id,
    type: referenceNodeToServer(data.type),
    label: data.label,
    integration_label: data.integrationLabel,
    to_be_deleted: data.toBeDeleted
  };
}
